import { Box, CircularProgress, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { parentFind } from "library/tree/index.mjs"
import { PlanRoot } from "library/tree/constants.mjs"
import { getShareLinkCommentRegimeId } from "routes/schedule/plugins/share-link-comments/share-link-comment-regime-id"
import { listShareLinkFieldResponsesForRegime } from "./share-link-fields.controller"
import { ResponsesGroup } from "./responses-group"

const EMPTY_GROUPS = []

/**
 * Internal reviewer's read-only list of requested-field answers grouped by share link. Embedded in the
 * regime Comments tab (`share-link-comments-regime-panel.js`) and can also be mounted standalone. It
 * resolves the regime via the shared `getShareLinkCommentRegimeId` helper, then loads the B6a aggregate
 * (`listShareLinkFieldResponsesForRegime`). The server enforces editor-only access and multi-tenant
 * isolation.
 */
export function ResponsesRegimePanel({
    title = "Responses",
    dataCy = "share-link-responses-panel",
    hideWhenEmpty = false,
    maxHeight = "55vh",
    sx,
    contentSx,
} = {}) {
    const { parentItem, facility, facilityGroup } = useBoundContext()
    const itemId = parentItem?.id ?? facility?.id ?? facilityGroup?.id
    const rootItem = parentFind(parentItem ?? facility ?? facilityGroup, isPlanRoot)
    const regimeId = getShareLinkCommentRegimeId({ itemId, rootItem })
    // Use the status variant, not the value + IS_LOADING sentinel: this is a side panel, so it must
    // never block the page with the global LoadingScreen, and useResults keeps IS_LOADING forever when
    // the query errors (e.g. no live regime / not authorised) — which previously hung the whole regime
    // Comments tab. On error `loading` clears and `data` is undefined, so we fall through to empty.
    const { data, loading } = listShareLinkFieldResponsesForRegime.useResults.status({ regimeId })

    if (!regimeId) return null
    if (loading) {
        if (hideWhenEmpty) return null
        return (
            <Box data-cy={dataCy} sx={sx}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} />
                </Box>
            </Box>
        )
    }

    const groups = getGroups(data)

    if (hideWhenEmpty && !groups.length) return null

    return (
        <Box data-cy={dataCy} sx={sx}>
            <Stack spacing={1.5}>
                {getTitle()}
                <Box data-cy={`${dataCy}-content`} sx={{ maxHeight, overflowY: "auto", pr: 1, ...contentSx }}>
                    <Stack spacing={2.5}>
                        <Repeat
                            list={groups}
                            pass="group"
                            keyFn={groupKey}
                            item={<ResponsesGroup />}
                            fallback={
                                <Typography variant="body2" color="text.secondary">
                                    No responses yet. Answers from share-link recipients will appear here.
                                </Typography>
                            }
                        />
                    </Stack>
                </Box>
            </Stack>
        </Box>
    )

    function getTitle() {
        if (!title) return null
        return <Typography variant="subtitle1">{title}</Typography>
    }
}

function getGroups(result) {
    if (!result) return EMPTY_GROUPS
    return Array.isArray(result.groups) ? result.groups : EMPTY_GROUPS
}

function groupKey(group, index) {
    return group?.shareLinkId ?? index
}

function isPlanRoot(item) {
    return !!item?.[PlanRoot]
}
